import type { EnergyEntryData } from "@features/modules/energy/types";
import { convertEmptyStringsToNull, convertOutgoingPercentage, convertIncomingPercentage } from "@shared/modulePayload";
import { EntryCreatePayload, EntryDeletePayload, EntryUpdatePayload, ParentUpdatePayload, ParentResponse, ParentPayload } from "../../../types/modulesInterfaces";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Define constants for the module
const MODULE_TYPE = "Energy";
const TAG: TagTypes = "Energies";
const ENDPOINT = FEModulesObj[FEModules.Energy].path;

const ENTRY_MODULE_TYPE = "EnergyEntries";
const TAG_ENTRY: TagTypes = "EnergyEntries";
const TAG_ENTRY_DEFAULTS: TagTypes = "EnergyEntriesDefaults";
export const ENTRY_ENDPOINT = "energy-entries";

const energyApiSlice = createModuleGetApi<ParentResponse>({
  apiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
});

const transformEntriesResponse = (response: EnergyEntryData[]) => {
  response.forEach(entry => convertIncomingPercentage(entry, ["transmission_loss_t2"]));
  return response.sort((a, b) => a.id - b.id);
};

const energyEntriesApiSlice = createEntriesGetApi<EnergyEntryData[]>({
  apiSlice,
  path: ENTRY_ENDPOINT,
  moduleName: ENTRY_MODULE_TYPE,
  tag: TAG_ENTRY,
  tagDefaults: TAG_ENTRY_DEFAULTS,
  transformResponse: transformEntriesResponse,
});

const publicEnergyApiSlice = createModuleGetApi<ParentResponse>({
  apiSlice: publicApiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
});

const publicEnergyEntriesApiSlice = createEntriesGetApi<EnergyEntryData[]>({
  apiSlice: publicApiSlice,
  path: ENTRY_ENDPOINT,
  moduleName: ENTRY_MODULE_TYPE,
  tag: TAG_ENTRY,
  tagDefaults: TAG_ENTRY_DEFAULTS,
  transformResponse: transformEntriesResponse,
});

export const { useGetEnergyQuery } = energyApiSlice;
export const { useGetEnergyEntriesQuery, useGetEnergyEntriesDefaultsQuery } = energyEntriesApiSlice;
export const { useGetEnergyQuery: usePublicGetEnergyQuery } = publicEnergyApiSlice;
export const { useGetEnergyEntriesQuery: usePublicGetEnergyEntriesQuery, useGetEnergyEntriesDefaultsQuery: usePublicGetEnergyEntriesDefaultsQuery } = publicEnergyEntriesApiSlice;

export const mutationApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateEnergies: builder.mutation<ParentResponse, ParentUpdatePayload<ParentPayload>>({
      query: ({ body }) => {
        const { id, ...rest } = body;
        convertEmptyStringsToNull(rest);
        return { url: `${ENDPOINT}/${id}/`, method: 'PUT', body: rest };
      },
      invalidatesTags: (_results, _error, { activityId, body }) => [
        { type: 'Energies', id: body?.id ?? 0 },
        { type: "ModulesStatuses", id: activityId },
        { type: 'ModuleResults', id: body?.id ? `energies-${body.id}` : undefined },
        { type: "ModuleHistory", id: body?.id ? `energies-${body.id}` : undefined },
      ]
    }),
    updateEnergyEntry: builder.mutation<EnergyEntryData, EntryUpdatePayload<EnergyEntryData>>({
      query: ({ body }) => {
        const { id, ...rest } = body;
        convertEmptyStringsToNull(rest);
        convertOutgoingPercentage(rest, ["transmission_loss_t2"]);
        return { url: `${ENTRY_ENDPOINT}/${id}/`, method: 'PUT', body: rest };
      },
      invalidatesTags: (_results, _error, { activityId, body }) => [
        { type: 'EnergyEntries', id: activityId },
        { type: "EnergyEntriesDefaults", id: body.id ? body.id : undefined },
      ]
    }),
    createEnergyEntry: builder.mutation<EnergyEntryData, EntryCreatePayload>({
      query: ({ activityId, parent }) => ({
        url: `${ENTRY_ENDPOINT}/`,
        method: 'POST',
        body: { activity: activityId, parent }
      }),
      invalidatesTags: (_results, _error, { activityId, parent }) => [
        { type: 'EnergyEntries', id: activityId },
        { type: 'ModuleResults', id: parent ? `energies-${parent}` : undefined },
        { type: "ModuleHistory", id: parent ? `energies-${parent}` : undefined },
        { type: "ModulesStatuses", id: activityId },
      ]
    }),
    deleteEnergyEntry: builder.mutation<void, EntryDeletePayload>({
      query: ({ id }) => ({ url: `${ENTRY_ENDPOINT}/${id}/`, method: 'DELETE' }),
      invalidatesTags: (_results, _error, { activityId, parent, id }) => [
        { type: 'EnergyEntries', id: activityId },
        { type: 'ModuleResults', id: parent ? `energies-${parent}` : undefined },
        { type: "ModuleHistory", id: parent ? `energies-${parent}` : undefined },
        { type: "EnergyEntriesDefaults", id },
        { type: "ModulesStatuses", id: activityId },
      ]
    }),
  }),
});

export const {
  useUpdateEnergiesMutation,
  useCreateEnergyEntryMutation,
  useUpdateEnergyEntryMutation,
  useDeleteEnergyEntryMutation,
} = mutationApiSlice;

export const energyEndpoints = energyApiSlice.endpoints;
export const energyEntriesEndpoints = energyEntriesApiSlice.endpoints;
export const energyMutationEndpoints = mutationApiSlice.endpoints;
